import { useState, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { FileText, Download, Calendar, Search, Filter, ExternalLink, FileCheck, Folder, ChevronRight, Eye } from 'lucide-react';

// Document categories
const CATEGORIES = [
  { id: 'official', name: 'Comunicados Oficiais', icon: FileCheck, color: 'bg-red-500' },
  { id: 'forms', name: 'Formulários', icon: FileText, color: 'bg-blue-500' },
  { id: 'laws', name: 'Legislação', icon: Folder, color: 'bg-green-500' },
  { id: 'guides', name: 'Guias e Manuais', icon: FileText, color: 'bg-purple-500' },
];

// Sample documents (in production, these would come from API)
const DOCUMENTS = [
  {
    id: 1,
    title: 'Comunicado - Novos Horários de Atendimento',
    category: 'official',
    date: '2026-02-15',
    description: 'Informamos que a partir de 1 de Março de 2026, o horário de atendimento será das 9h às 15h, de Segunda a Sexta.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 245
  },
  {
    id: 2,
    title: 'Formulário de Pedido de Cartão Consular',
    category: 'forms',
    date: '2026-01-10',
    description: 'Formulário oficial para solicitação de cartão consular angolano.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 1520
  },
  {
    id: 3,
    title: 'Formulário de Pedido de Visto',
    category: 'forms',
    date: '2026-01-10',
    description: 'Formulário para solicitação de visto de entrada em Angola.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 892
  },
  {
    id: 4,
    title: 'Lei da Nacionalidade Angolana',
    category: 'laws',
    date: '2025-06-20',
    description: 'Lei n.º 1/05, de 1 de Julho - Lei da Nacionalidade.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 432
  },
  {
    id: 5,
    title: 'Guia do Cidadão Angolano em Espanha',
    category: 'guides',
    date: '2025-12-01',
    description: 'Manual completo com informações úteis para cidadãos angolanos residentes em Espanha.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 678
  },
  {
    id: 6,
    title: 'Comunicado - Emissão de Passaportes',
    category: 'official',
    date: '2026-02-01',
    description: 'Informação sobre o processo de emissão e renovação de passaportes angolanos.',
    file_url: '#',
    file_type: 'pdf',
    downloads: 1234
  },
];

export default function DocumentsPage() {
  const { t } = useLanguage();
  const [documents, setDocuments] = useState(DOCUMENTS);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredDocuments = documents.filter(doc => {
    if (selectedCategory && doc.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return doc.title.toLowerCase().includes(query) || doc.description.toLowerCase().includes(query);
    }
    return true;
  });

  const getCategoryInfo = (categoryId) => CATEGORIES.find(c => c.id === categoryId);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Documentos e Comunicados</h1>
          <p className="text-gray-400 text-sm md:text-base">
            Aceda a formulários, comunicados oficiais e documentação relevante
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-gray-200 p-5 sticky top-8">
              <h3 className="font-semibold text-gray-900 mb-4">Categorias</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedCategory('')}
                  className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                    !selectedCategory ? 'bg-[#CC0000] text-white' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="text-sm font-medium">Todos os Documentos</span>
                  <span className="text-xs">{documents.length}</span>
                </button>
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const count = documents.filter(d => d.category === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                        selectedCategory === cat.id ? 'bg-[#CC0000] text-white' : 'hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        <span className="text-sm font-medium">{cat.name}</span>
                      </div>
                      <span className="text-xs">{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Pesquisar documentos..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none"
                />
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {filteredDocuments.length} documento(s) encontrado(s)
              </p>
            </div>

            {/* Documents Grid */}
            <div className="space-y-4">
              {filteredDocuments.map(doc => {
                const catInfo = getCategoryInfo(doc.category);
                const CatIcon = catInfo?.icon || FileText;
                return (
                  <div 
                    key={doc.id}
                    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
                    data-testid={`document-${doc.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 ${catInfo?.color || 'bg-gray-500'} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <CatIcon size={24} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-gray-900 mb-1">{doc.title}</h3>
                            <p className="text-sm text-gray-500 mb-2">{doc.description}</p>
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(doc.date).toLocaleDateString('pt-PT')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Download size={12} />
                                {doc.downloads} downloads
                              </span>
                              <span className={`px-2 py-0.5 rounded-full ${catInfo?.color || 'bg-gray-500'} text-white`}>
                                {catInfo?.name}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                              <Eye size={18} />
                            </button>
                            <a 
                              href={doc.file_url}
                              download
                              className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000] transition-colors"
                            >
                              <Download size={16} />
                              PDF
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {filteredDocuments.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <FileText size={48} className="mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum documento encontrado</h3>
                  <p className="text-sm text-gray-500">Tente ajustar os filtros de pesquisa</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
